import { useCallback, useEffect, useRef, useState } from 'react';
import { Search, X, Trash2, Pencil, MessageSquare, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import {
  listConversations,
  deleteConversation as apiDeleteConversation,
  renameConversation as apiRenameConversation,
  type ConversationSummary,
} from '@/services/chat';
import { extractErrorMessage } from '@/services/api';
import { useChatStore } from '@/stores/chatStore';

interface Props {
  open: boolean;
  onClose: () => void;
}

// ConversationHistory is a slide-in drawer that lists past chats,
// supports search + rename + delete, and loads any conversation back
// into the current chat store on click. Renders as a fixed-position
// overlay covering most of the taskpane since the pane is narrow.
export function ConversationHistory({ open, onClose }: Props) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<ConversationSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const { conversationId, loadConversation, resetConversation } = useChatStore();
  const searchRef = useRef<HTMLInputElement | null>(null);

  // Debounce the search query so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 250);
    return () => clearTimeout(t);
  }, [query]);

  const refresh = useCallback(async (q: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await listConversations({ q: q || undefined, limit: 100 });
      setItems(res.conversations);
    } catch (e) {
      setError(extractErrorMessage(e, 'Failed to load history'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      refresh(debounced);
    }
  }, [open, debounced, refresh]);

  // Focus the search box when the drawer opens so the user can type
  // without extra clicks.
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => searchRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Close on Escape for keyboard-friendliness.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const onOpen = async (id: string) => {
    if (id === conversationId) {
      onClose();
      return;
    }
    try {
      await loadConversation(id);
      onClose();
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Failed to open conversation'));
    }
  };

  const onDelete = async (id: string) => {
    if (deleting === id) return;
    setDeleting(id);
    try {
      await apiDeleteConversation(id);
      setItems((xs) => xs.filter((x) => x.id !== id));
      if (id === conversationId) {
        resetConversation();
      }
      toast.success('Conversation deleted');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Delete failed'));
    } finally {
      setDeleting(null);
    }
  };

  const onRename = async (id: string, nextTitle: string) => {
    const title = nextTitle.trim();
    if (!title) {
      setRenaming(null);
      return;
    }
    try {
      await apiRenameConversation(id, title);
      setItems((xs) => xs.map((x) => (x.id === id ? { ...x, title } : x)));
      toast.success('Renamed');
    } catch (e) {
      toast.error(extractErrorMessage(e, 'Rename failed'));
    } finally {
      setRenaming(null);
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-black/30 backdrop-blur-[1px]"
        onClick={onClose}
        aria-hidden
      />
      <aside
        className="fixed left-0 top-0 bottom-0 z-50 flex w-[86%] max-w-[380px] flex-col border-r border-border bg-white shadow-card dark:border-border-dark dark:bg-surface-dark"
        role="dialog"
        aria-label="Conversation history"
      >
        <header className="flex items-center justify-between gap-2 border-b border-border px-3 py-2 dark:border-border-dark">
          <h2 className="text-sm font-semibold">History</h2>
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost text-xs"
            aria-label="Close history"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <div className="border-b border-border px-3 py-2 dark:border-border-dark">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-text-secondary" />
            <input
              ref={searchRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search title or message…"
              className="w-full rounded-md border border-border bg-transparent py-1.5 pl-7 pr-2 text-xs outline-none focus:border-primary dark:border-border-dark"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading && (
            <div className="flex h-20 items-center justify-center text-xs text-text-secondary">
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> Loading…
            </div>
          )}
          {error && (
            <div className="px-3 py-4 text-xs text-red-500">{error}</div>
          )}
          {!loading && !error && items.length === 0 && (
            <div className="px-3 py-8 text-center text-xs text-text-secondary">
              {debounced
                ? `No matches for "${debounced}"`
                : 'No conversations yet — start chatting.'}
            </div>
          )}
          <ul className="divide-y divide-border dark:divide-border-dark">
            {items.map((c) => (
              <ConversationRow
                key={c.id}
                item={c}
                active={c.id === conversationId}
                renaming={renaming === c.id}
                onStartRename={() => setRenaming(c.id)}
                onCancelRename={() => setRenaming(null)}
                onRename={(next) => onRename(c.id, next)}
                onDelete={() => onDelete(c.id)}
                onOpen={() => onOpen(c.id)}
                deleting={deleting === c.id}
              />
            ))}
          </ul>
        </div>
      </aside>
    </>
  );
}

function ConversationRow({
  item, active, renaming, deleting,
  onOpen, onStartRename, onCancelRename, onRename, onDelete,
}: {
  item: ConversationSummary;
  active: boolean;
  renaming: boolean;
  deleting: boolean;
  onOpen: () => void;
  onStartRename: () => void;
  onCancelRename: () => void;
  onRename: (next: string) => void;
  onDelete: () => void;
}) {
  const [draft, setDraft] = useState(item.title || '');

  useEffect(() => {
    if (renaming) setDraft(item.title || '');
  }, [renaming, item.title]);

  const title = item.title?.trim() || 'Untitled';
  const updated = formatRelativeTime(item.updated_at);
  const subLine = [item.provider, item.model].filter(Boolean).join(' · ');

  return (
    <li
      className={[
        'group flex items-start gap-2 px-3 py-2 text-xs',
        active ? 'bg-primary/5 dark:bg-primary/10' : 'hover:bg-black/5 dark:hover:bg-white/5',
      ].join(' ')}
    >
      <MessageSquare
        className={[
          'mt-0.5 h-3.5 w-3.5 flex-shrink-0',
          active ? 'text-primary' : 'text-text-secondary',
        ].join(' ')}
      />
      <button
        type="button"
        onClick={onOpen}
        className="min-w-0 flex-1 text-left"
        disabled={renaming}
        title={title}
      >
        {renaming ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              onRename(draft);
            }}
            className="flex items-center gap-1"
          >
            <input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  onCancelRename();
                }
              }}
              className="w-full rounded border border-border bg-transparent px-1 py-0.5 text-xs outline-none focus:border-primary dark:border-border-dark"
            />
            <button
              type="submit"
              className="rounded p-0.5 text-primary hover:bg-primary/10"
              aria-label="Save title"
            >
              <Check className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={onCancelRename}
              className="rounded p-0.5 text-text-secondary hover:bg-black/5 dark:hover:bg-white/10"
              aria-label="Cancel rename"
            >
              <X className="h-3 w-3" />
            </button>
          </form>
        ) : (
          <>
            <div className="truncate font-medium">{title}</div>
            <div className="mt-0.5 flex items-center gap-2 text-[10px] text-text-secondary">
              <span>{updated}</span>
              {subLine && <span className="truncate">· {subLine}</span>}
            </div>
          </>
        )}
      </button>

      {!renaming && (
        <div className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          <button
            type="button"
            onClick={onStartRename}
            className="rounded p-1 text-text-secondary hover:bg-black/5 hover:text-primary dark:hover:bg-white/10"
            aria-label="Rename"
            title="Rename"
          >
            <Pencil className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={deleting}
            className="rounded p-1 text-text-secondary hover:bg-red-500/10 hover:text-red-500"
            aria-label="Delete"
            title="Delete"
          >
            {deleting ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Trash2 className="h-3 w-3" />
            )}
          </button>
        </div>
      )}
    </li>
  );
}

// formatRelativeTime turns an ISO timestamp into "just now", "3m ago",
// "yesterday", "Mar 4". Kept local so we don't pull in a date library.
function formatRelativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diffSec = Math.floor((Date.now() - t) / 1000);
  if (diffSec < 45) return 'just now';
  if (diffSec < 60 * 60) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 60 * 60 * 24) return `${Math.floor(diffSec / 3600)}h ago`;
  const diffDays = Math.floor(diffSec / (60 * 60 * 24));
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
